// Identity voices API (ADR-0077): the voice twin of the identity faces endpoints.
//
//   GET  /v1/cases/{caseId}/unassigned-voices                      — every voice nobody is confirmed on
//   GET  /v1/cases/{caseId}/identities/{id}/voices                  — the identity's voiceprints
//   POST /v1/cases/{caseId}/identities/{id}/voices                  — confirm a voice selection to it
//   POST /v1/cases/{caseId}/identities/from-voices                  — a new identity from a selection
//   PUT  /v1/cases/{caseId}/identities/{id}/voices/{vp}/reference   — the reference switch
//   GET  /v1/cases/{caseId}/identities/{id}/voices/{vp}/evidence    — the small graph of one voiceprint
//
// Every route sits behind the HUMINT extension gate; a viewer reads and listens, a
// collaborator decides. Imported by path, not via the barrel.
import type { CaseRole } from './cases'
import { apiClient } from './client'
import type { GraphData } from './graph'

/** The strongest person the matcher still proposes for a voice. */
export interface VoiceSuggestion {
  person_id: string
  name: string | null
  similarity: number | null
  band: string | null
}

/** One row of the *Unassigned voices* tab. */
export interface UnassignedVoice {
  voice_id: string
  voice: number
  document_id: string
  document_name: string | null
  recorded_at: string | null
  speech_seconds: number | null
  matching_status: string | null
  short_evidence: boolean
  attribution_conflict: boolean
  voiceprint_id: string | null
  /** The audited clip route of the voice; null while there is no clip. */
  audio_url: string | null
  playable: boolean
  /** The voice has a voiceprint, so it can be confirmed to a person. */
  assignable: boolean
  pending_suggestions: number
  best_suggestion: VoiceSuggestion | null
}

export interface UnassignedVoicesPage {
  voices: UnassignedVoice[]
  total: number
  page: number
  page_size: number
  member_role: CaseRole
}

/** Why a voiceprint cannot be a reference. */
export type ReferenceBlocker = 'no_vector' | 'short_speech' | 'low_quality'

/** One voiceprint of the identity's Voices card. */
export interface IdentityVoiceprint {
  voiceprint_id: string
  origin: 'sample' | 'recording'
  voice: number | null
  document_id: string
  document_name: string | null
  recorded_at: string | null
  quality: string | null
  quality_flags: string[]
  speech_seconds: number | null
  reference: boolean
  /** Why it cannot be a reference; null when it can, or already is. */
  reference_blocker: ReferenceBlocker | null
  playable: boolean
  audio_url: string | null
  /** A file name whose suffix names the audio type of what plays. */
  audio_name: string
}

export interface IdentityVoicesResponse {
  person_id: string
  voiceprints: IdentityVoiceprint[]
  member_role: CaseRole
}

/** The identity after a manual create or an assign. */
export interface ManualVoiceIdentity {
  person_id: string
  name: string | null
  first_name: string | null
  last_name: string | null
  nickname: string | null
  unnamed_seq: number | null
  confirmed_voiceprint_ids: string[]
}

const identityPath = (caseId: string, identityId: string) =>
  `/v1/cases/${caseId}/identities/${encodeURIComponent(identityId)}/voices`

export const identityVoicesApi = {
  unassigned: (caseId: string, params: { page?: number; pageSize?: number } = {}) =>
    apiClient.get<UnassignedVoicesPage>(`/v1/cases/${caseId}/unassigned-voices`, {
      params: { page: params.page ?? 1, page_size: params.pageSize ?? 25 },
    }),

  list: (caseId: string, identityId: string, signal?: AbortSignal) =>
    apiClient.get<IdentityVoicesResponse>(identityPath(caseId, identityId), { signal }),

  /** Confirm a voice selection to an identity that already exists; a voice held elsewhere is a 409. */
  assign: (caseId: string, identityId: string, voiceprintIds: string[]) =>
    apiClient.post<ManualVoiceIdentity>(identityPath(caseId, identityId), {
      voiceprint_ids: voiceprintIds,
    }),

  /** Create a manual identity out of a voice selection; all three names blank is a 422. */
  createFromVoices: (
    caseId: string,
    body: { first_name?: string; last_name?: string; nickname?: string; voiceprint_ids: string[] }
  ) => apiClient.post<ManualVoiceIdentity>(`/v1/cases/${caseId}/identities/from-voices`, body),

  /** The reference switch; switching on without enough clean speech is a 409 `not_eligible`. */
  setReference: (caseId: string, identityId: string, voiceprintId: string, reference: boolean) =>
    apiClient.put<IdentityVoicesResponse>(
      `${identityPath(caseId, identityId)}/${encodeURIComponent(voiceprintId)}/reference`,
      { reference }
    ),

  evidence: (caseId: string, identityId: string, voiceprintId: string) =>
    apiClient.get<GraphData>(
      `${identityPath(caseId, identityId)}/${encodeURIComponent(voiceprintId)}/evidence`
    ),
}
